import { ref, shallowRef, reactive, watch } from 'vue'
import { useTemplate, useFormModify } from '../../../hooks/index'
import {
  http,
  get,
  setValueByPath,
  setValueByPathToVue,
  getValueByPath,
  clone,
} from '../../../utils/index'
import { useRoute, useRouter } from 'vue-router/composables'
import { useTable } from './use-table'
import { pgMessage } from '../../message/index'

export function useForm({ formItems, formItemsUrl, formItemsCode, url, urlMethod, urlParamter }) {
  const route = useRoute()
  const { getTemplateData, compileData } = useTemplate()
  const nowFormItems = shallowRef({})
  const formData = ref({})
  const getFromData = async (newQuery) => {
    if (!url) {
      pgMessage.error('未配置url')
      return {}
    } else {
      const tempQuery = newQuery ? newQuery : route.query
      const [res] = await http[urlMethod](url, { ...tempQuery, ...urlParamter })
      return res?.data ?? {}
    }
  }

  const { setFormModifyBy, clearFormModifyBy } = useFormModify()

  const {
    tableCompileData,
    tableStaticData,
    setInitTableCompileData,
    loadTable,
    fullValid,
    addTableRows,
    removeTableRows,
    replaceRows,
    copyRows,
    copyChildRows,
  } = useTable()
  const setDefaultValue = (data, items) => {
    const itemsList = Array.isArray(items) ? items : items.items
    const templateData = getTemplateData(data)
    for (let item of itemsList) {
      if (item.visible && item.defaultValue && getValueByPath(data, item.property, null) === null) {
        setValueByPath(data, item.property, compileData(item.defaultValue, templateData))
      }
    }
    formData.value = setInitTableCompileData(itemsList, data)
    clearFormModifyBy()
    loadTable()
  }

  const router = useRouter()
  // 重新获取数据，当param.router存在且param.router.id !== route.query?.id时会更新route.query?.id
  const reload = async (param) => {
    let tempQuery = null
    if (param.router && param.router.id !== route.query?.id) {
      tempQuery = { id: param.router.id }
      router.replace({ name: param.router?.name ?? route.name, query: tempQuery })
    }
    const data = await getFromData(tempQuery)
    setDefaultValue(data, nowFormItems.value)
  }

  const getFormItem = async () => {
    if (formItems) {
      return formItems
    } else if (formItemsCode) {
      const [resForm] = await get(`${formItemsUrl}${formItemsCode}`)
      return resForm?.data ?? {}
    } else {
      throw '未配置表单定义formItemsCode或formItems'
    }
  }

  const initLoad = async () => {
    const [data, form] = await Promise.all([getFromData(), getFormItem()])
    nowFormItems.value = form
    setDefaultValue(data, nowFormItems.value)
  }

  const updateFormData = (data) => {
    const itemsList = Array.isArray(nowFormItems.value)
      ? nowFormItems.value
      : nowFormItems.value.items
    formData.value = setInitTableCompileData(itemsList, data)
    loadTable()
  }

  const formReactive = reactive({ formData, tableStaticData })

  const getAllFormData = () => {
    const copy = clone(formData.value)
    Object.keys(tableStaticData.value).forEach((key) => {
      //如果配置的字段是以__开头则不添加到整单数据中
      if (!key.startsWith('__')) {
        setValueByPath(copy, key, clone(tableStaticData.value[key]))
      }
    })
    return copy
  }

  // 表单校验方法
  const showTagName = ref(null)
  const formRefs = {}
  const formComplete = (key, validate) => {
    formRefs[key] = validate
  }
  const tagValidateTips = shallowRef(null)
  const validate = async () => {
    tagValidateTips.value = null
    const shwoTagErrors = await formRefs[showTagName.value](fullValid)
    if (!shwoTagErrors) {
      const tagHideKeys = Object.keys(formRefs).filter((im) => im !== showTagName.value)
      const validateTask = await Promise.all(tagHideKeys.map((key) => formRefs[key](fullValid)))
      tagValidateTips.value = validateTask.reduce((acc, cur, index) => {
        if (cur) {
          acc[tagHideKeys[index]] = '请完善必填信息！'
        }
        return acc
      }, {})

      if (Object.keys(tagValidateTips.value).length > 0) {
        pgMessage.error('请完善必填信息！')
        return true
      }
    } else if (shwoTagErrors) {
      pgMessage.error('请完善必填信息！')
      return shwoTagErrors
    }
  }
  //变化时清空校验提示
  watch(showTagName, () => {
    if (tagValidateTips.value?.[showTagName.value]) {
      tagValidateTips.value[showTagName.value] = null
    }
  })

  /**设置formData对应属于的值，支持批量设置可以传入数组，如 setFormDataFieldVal(['a.b','c'], ['val', 'val'])
   * @param {string|string[]} field
   * @param {string|string[]} val
   */
  const setFormDataFieldVal = (field, val) => {
    const itemsList = Array.isArray(nowFormItems.value)
      ? nowFormItems.value
      : nowFormItems.value.items
    const isArray = Array.isArray(field)
    const fieldArr = isArray ? field : [field]
    const valArr = isArray ? val : [val]
    for (let i = 0; i < fieldArr.length; i++) {
      const item = itemsList.find((im) => im.property === fieldArr[i])
      if (
        item?.componentCode === 'PgPageEditTable' ||
        item?.component?.code === 'PgPageEditTable' ||
        item?.component?.code === 'PgPageEditTableDrag'
      ) {
        const tempFormData = getAllFormData()
        setValueByPath(tempFormData, fieldArr[i], valArr[i])
        updateFormData(tempFormData)
      } else {
        setValueByPathToVue(formData.value, fieldArr[i], valArr[i])
      }
    }
  }

  return {
    nowFormItems, //表单项配置列表
    formReactive, //包括表格的表单数据
    tableCompileData, // 表格数据的key数组和选中项key数组(默认_ID,支持在表格组件进行配置)
    loadTable, //重新渲染表格方法
    validate, // 表单校验方法
    showTagName, //表单ref
    tagValidateTips, //标签必填校验提示信息
    formComplete, // 表单渲染完成后的回调钩子
    reload, // 刷新表格数据方法
    initLoad, // 初始化
    updateFormData, // 手动更新表单数据
    getAllFormData, //获取表单的全部数据（只读，不要修改通过该方法获取的数据）
    setFormDataFieldVal, //设置formData对应属于的值
    setFormModifyBy,
    addTableRows,
    removeTableRows,
    replaceRows,
    copyRows,
    copyChildRows,
  }
}
